import { useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CHAPTERS = [
  {
    label: 'Chapter One',
    title: 'Career',
    text: 'For over 30 years, Manvinder has been shaped by a career in leadership and mentoring. Her experience has been grounded in supporting people, navigating change, and understanding what enables individuals to grow and thrive.',
    image: '/images/7.webp',
  },
  {
    label: 'Chapter Two',
    title: 'Identity',
    text: 'Mother. Daughter. Professional. Friend. She wore every role beautifully — until the roles became the whole story, and the woman underneath began to fade from her own life.',
    image: '/images/8.webp',
  },
  {
    label: 'Chapter Three',
    title: 'Loss',
    text: 'Then came the season that broke everything open. The grief no one saw. The mornings that felt impossible. In losing what she thought defined her, she was forced to ask — who am I, when everything falls away?',
    image: '/images/2.webp',
  },
  {
    label: 'Chapter Four',
    title: 'Awakening',
    text: 'In the stillness that followed, something unexpected happened. She stopped running. She began to listen. Presence — real, unhurried presence — became her way back. Not to who she was. To who she had always been.',
    image: '/images/10.webp',
  },
  {
    label: 'Chapter Five',
    title: 'Still Becoming',
    text: 'Trained as an Ontological Coach and having completed the Inner MBA from Sounds True, Manvinder continues to deepen her understanding of wellbeing, presence, and human development. These experiences, alongside her years in leadership and mentoring, shape how she works today.',
    image: '/images/13.webp',
  },
];

const About = () => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add('(min-width: 768px)', () => {
        gsap.utils.toArray('.chapter').forEach((chapter) => {
          const img = chapter.querySelector('.chapter-img-wrap');
          const inner = chapter.querySelector('.chapter-img-wrap img');
          const content = chapter.querySelector('.chapter-content');

          gsap.fromTo(img, { clipPath: 'inset(100% 0 0 0)' }, {
            clipPath: 'inset(0% 0 0 0)',
            ease: 'none',
            scrollTrigger: { trigger: chapter, start: 'top 75%', end: 'top 15%', scrub: 1.2 },
          });
          gsap.fromTo(inner, { scale: 1.18, y: 40 }, {
            scale: 1, y: -40, ease: 'none',
            scrollTrigger: { trigger: chapter, start: 'top bottom', end: 'bottom top', scrub: 1.5 },
          });
          gsap.fromTo(content.children, { opacity: 0, y: 50 }, {
            opacity: 1, y: 0, stagger: 0.18, duration: 1.6, ease: 'power2.out',
            scrollTrigger: { trigger: chapter, start: 'top 55%' },
          });
        });
      });
      mm.add('(max-width: 767px)', () => {
        gsap.utils.toArray('.chapter').forEach((chapter) => {
          gsap.fromTo(chapter.querySelector('.chapter-img-wrap'), { clipPath: 'inset(100% 0 0 0)' }, {
            clipPath: 'inset(0% 0 0 0)', duration: 1.6, ease: 'power2.out',
            scrollTrigger: { trigger: chapter, start: 'top 70%' },
          });
          gsap.fromTo(chapter.querySelector('.chapter-content'), { opacity: 0, y: 40 }, {
            opacity: 1, y: 0, duration: 1.4, ease: 'power2.out',
            scrollTrigger: { trigger: chapter, start: 'top 60%' },
          });
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={root} data-testid="about-section" className="relative bg-[#FAFAF8] py-32 md:py-48">
      <div className="px-6 md:px-16 mb-24 md:mb-40 max-w-5xl">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#B85C38] mb-8">The Story · Manvinder Randhawa</p>
        <h2 className="font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.08] text-[#2C2C2A]">
          Every homecoming
          <br />
          begins with <em className="text-[#1A4D38]">getting lost.</em>
        </h2>
      </div>

      <div className="space-y-32 md:space-y-0">
        {CHAPTERS.map((ch, i) => (
          <div
            key={ch.title}
            data-testid={`about-chapter-${ch.title.toLowerCase().replace(/\s/g, '-')}`}
            className={`chapter md:min-h-screen flex flex-col md:flex-row items-center gap-10 md:gap-0 px-6 md:px-16 py-0 md:py-24 ${
              i % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}
          >
            <div className={`w-full md:w-[44%] ${i % 2 === 1 ? 'md:pl-20' : 'md:pr-20'}`}>
              <div className="chapter-img-wrap overflow-hidden will-change-[clip-path]" style={{ clipPath: 'inset(100% 0 0 0)' }}>
                <img
                  src={ch.image}
                  alt={ch.founder ? 'Manvinder Randhawa, founder of Rise With Presence' : ch.title}
                  loading="lazy"
                  className={`w-full object-cover will-change-transform ${ch.founder ? 'aspect-[3/4] object-top' : 'aspect-[3/4]'}`}
                />
              </div>
              {ch.founder && (
                <p className="mt-6 text-xs uppercase tracking-[0.3em] text-[#2C2C2A]/50">Manvinder Randhawa · Founder</p>
              )}
            </div>
            <div className="chapter-content w-full md:w-[56%] max-w-xl">
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#7A9E86] mb-6">{ch.label}</p>
              <h3 className="font-serif-display font-light text-4xl md:text-6xl text-[#2C2C2A] mb-8 md:mb-10">{ch.title}</h3>
              <p className="text-base md:text-lg leading-[1.9] text-[#2C2C2A]/75 font-light">{ch.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default About;
